import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { categoryAPI, userAPI } from '../../services/api';
import Filter from '../../components/ui/Filter';

const TransactionFilters = ({
  filters,
  onFiltersChange,
  onExport,
  onReset,
  userRole = 'MANAGER', // Default to MANAGER for admin view
  user, // Add user prop to access caisse information
  hasActiveFilters = false,
  hasSelection = false,
  transactions
}) => {
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFilterOptions = async () => {
      setLoading(true);
      setError(null);
      try {
               // Use different API calls based on user role
               if (userRole === 'AGENT') {
                 const caisseId = user?.assignedCaisses?.[0]?.id;

                 if (!caisseId) {
                   setLoading(false);
                   return;
                 }

                 const [categoriesRes, usersRes] = await Promise.all([
                   categoryAPI.getForFiltersByCaisse(caisseId),
                   userAPI.getForFiltersByCaisseTransactions([caisseId])
                 ]);

                 setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);

                 // Users are already filtered by the backend (desk agents + managers who created transactions)
                 setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
                 } else if (userRole === 'VERIFICATION') {
                 // For verification agents, get pointage categories for their assigned caisses
                 const assignedCaisseIds = user?.assignedCaisses?.map(caisse => caisse.id) || [];

                 if (assignedCaisseIds.length === 0) {
                   setLoading(false);
                   return;
                 }

                 const [categoriesRes, usersRes] = await Promise.all([
                   categoryAPI.getForFilters({ 
                     pointageOnly: true, 
                     caisseIds: assignedCaisseIds 
                   }),
                   userAPI.getForFiltersByCaisseTransactions(assignedCaisseIds)
                 ]);

                 setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);

                 // Users are already filtered by the backend
                 setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
               } else {
                 // For managers, get all categories
                 const [categoriesRes, usersRes] = await Promise.all([
                   categoryAPI.getForFilters(),
                   userAPI.getForFilters()
                 ]);

                 setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);

                 // Filter users to show desk agents and managers (both can create transactions)
                 const allUsers = Array.isArray(usersRes.data) ? usersRes.data : [];
                 const relevantUsers = allUsers.filter(user => user.role === 'AGENT' || user.role === 'MANAGER');
                 setUsers(relevantUsers);
               }
      } catch (error) {
        console.error('Error loading filter options:', error);
        setError('Erreur lors du chargement des filtres');
      } finally {
        setLoading(false);
      }
    };

    loadFilterOptions();
  }, [userRole, user]);

  const handleFilterChange = (filterName, value) => {
    let updatedFilters = {
      ...filters,
      [filterName]: value
    };

    // Smart category filtering logic
    if (filterName === 'categoryIds') {
      // When category filter changes, expand to include all categories with the same label
      if (value && value.length > 0) {
        const expandedCategoryIds = [];
        value.forEach(categoryId => {
          const selectedCategory = categories.find(cat => cat.id === categoryId);
          if (selectedCategory) {
            // Find all categories with the same label
            const sameLabelCategories = categories.filter(cat => 
              cat.label === selectedCategory.label
            );
            sameLabelCategories.forEach(cat => {
              if (!expandedCategoryIds.includes(cat.id)) {
                expandedCategoryIds.push(cat.id);
              }
            });
          }
        });
        updatedFilters.categoryIds = expandedCategoryIds;
      }
    }

    onFiltersChange(updatedFilters);
  };

  // Get category options based on current caisse selection
  const getCategoryOptions = () => {
    const selectedCaisseIds = filters.caisseIds || [];
    
    if (selectedCaisseIds.length === 0) {
      // No caisse selected - show unique category names
      const uniqueCategories = categories.reduce((acc, category) => {
        if (!acc.find(cat => cat.label === category.label)) {
          acc.push(category);
        }
        return acc;
      }, []);
      
      return uniqueCategories.map(category => ({
        id: category.id,
        value: category.id,
        label: category.label
      }));
    } else {
      // Caisse(s) selected - show only categories for those caisses
      const caisseCategories = categories.filter(category => {
        const caisseId = category.caisse?.id;
        return selectedCaisseIds.includes(caisseId);
      });
      
      return caisseCategories.map(category => ({
        id: category.id,
        value: category.id,
        label: category.label
      }));
    }
  };




        // Define filter configuration
        const baseFilters = [
          {
            name: 'searchTerm',
            type: 'search',
            label: 'Rechercher',
            placeholder: 'Recherche...',
            isMultiSelect: false
          },
          {
            name: 'categoryIds',
            type: 'dropdown',
            label: 'Catégorie',
            placeholder: 'Catégorie: Toutes',
            isMultiSelect: true,
            options: getCategoryOptions()
          }
        ];

        // Add remaining filters
        baseFilters.push(
          {
            name: 'userIds',
            type: 'dropdown',
            label: 'Utilisateur',
            placeholder: 'Utilisateur: Tous',
            isMultiSelect: true,
            options: Array.isArray(users) ? users.map(user => ({
              id: user.id,
              value: user.id,
              label: `${user.firstName} ${user.lastName}`
            })) : []
          }
        );

        // Add status pointage filter for desk agents
        if (userRole === 'AGENT') {
          baseFilters.push({
            name: 'verificationStatus',
            type: 'dropdown',
            label: 'Status pointage',
            placeholder: 'Status pointage: Tous',
            isMultiSelect: false,
            isSearchable: false,
            options: [
              { value: '', label: 'Tous' },
              { value: 'en_cours_de_verification', label: 'En cours de pointage' }
            ]
          });
        }

        // Add status filter only for VERIFICATION users
        if (userRole === 'VERIFICATION') {
          baseFilters.push({
            name: 'verificationStatus',
            type: 'dropdown',
            label: 'Statut',
            placeholder: 'Statut: Tous',
            isMultiSelect: false,
            isSearchable: false,
            options: [
              { id: 'OPEN', value: 'ouvert', label: 'Ouvert' },
              { id: 'IN_PROGRESS', value: 'en_cours_de_verification', label: 'En cours' },
              { id: 'VERIFIED', value: 'verifie', label: 'Vérifié' }
            ]
          });
        }

        // Add date filter
        baseFilters.push({
          name: 'dateRange',
          type: 'daterange',
          label: 'Date',
          placeholder: 'Date',
          isMultiSelect: false
        });

        // Add pointage filter only for MANAGER users (not for AGENT or VERIFICATION)
        if (userRole === 'MANAGER') {
          baseFilters.push({
            name: 'requiresPointage',
            type: 'dropdown',
            label: 'Pointage',
            placeholder: 'Pointage: Tous',
            isMultiSelect: false,
            isSearchable: false,
            options: [
              { value: '', label: 'Tous' },
              { value: 'true', label: 'Nécessite Pointage' },
              { value: 'false', label: 'Pas de Pointage' }
            ]
          });
        }

        const filterOptions = baseFilters;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 0, width: '100%', maxWidth: '100%', overflow: 'visible' }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Component */}
      <Filter
        filters={filterOptions}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onReset={onReset}
        showApplyButton={false}
        onExport={userRole === 'AGENT' ? null : onExport}
        exportFormats={userRole === 'VERIFICATION' ? ['CSV'] : undefined}
        hasActiveFilters={hasActiveFilters}
        hasSelection={hasSelection}
        hasResults={transactions && transactions.length > 0}
      />

    </Box>
  );
};

export default TransactionFilters;
